/**
 * THE FACILITATOR OS STATUS VOCABULARY. One word per state, and why that word.
 *
 * ===================== THE DEFECT THIS FIXES ===============================
 * The audit's third system defect: the submitted-but-not-graded state carries
 * SEVEN surface names, the graded state four, the pace comparison four
 * phrasings, and `active` names two unrelated things, one of which is a data
 * integrity failure. A facilitator moving Coach to Review to Prep re-learns the
 * vocabulary three times.
 *
 * ===================== THIS CHUNK DEFINES, IT DOES NOT APPLY ===============
 * Amir's ruling, 2026-08-06: no standalone vocabulary PR. The vocabulary is
 * defined centrally here and PINNED page by page inside FE-9.3 (Review),
 * FE-9.4 (Manage) and FE-9.5 (Coach and Prep), where each rename can be
 * reviewed against the surface it changes. FE-9.6 is the cold cross-surface
 * sweep that verifies the result. So this module deliberately has ZERO
 * consumers today, and `RETIRED` is the map that makes each later rename
 * mechanical rather than a judgement call re-made three times.
 *
 * ===================== WHAT A "STATE" IS HERE ==============================
 * A key is an UNDERLYING condition, not a phrase. Two phrases that describe the
 * same condition collapse to one key; one phrase used for two conditions splits
 * into two keys. That is the entire operation, and it is why `active` appears
 * once (Coach's activity sense) and Review's integrity fallback gets its own
 * word: they were never the same state.
 *
 * ===================== THE THREE AXES, AND WHY THEY DO NOT COLLIDE =========
 * A squad sits on three independent axes at once, and words that look like
 * duplicates across axes are not:
 *
 *   WORK STAGE, where a squad is in its own week: planning, executing,
 *   finishing, turned in. Four positions on one line, and every squad occupies
 *   exactly one.
 *
 *   REVIEW, what the teacher owes: awaiting review, reviewed, not turned in.
 *   This axis only exists once work has been handed in, or has failed to be.
 *
 *   PACE, the level a squad has evidenced against the level the teacher
 *   expects: ahead of pace, at pace, behind pace.
 *
 * `turned in` (stage) and `awaiting review` (review) are the same underlying
 * submission described on two axes, and both survive because a facilitator
 * scanning the room asks a different question than a facilitator working a
 * queue. What does NOT survive is `submitted`, `work turned in`, `review
 * needed`, `work awaiting review`, `scored N%` and `current score N%`: six
 * phrasings that added no axis at all.
 */

/**
 * Tone names are `StatusChip`'s, checked by the coverage test so a tone cannot
 * be invented here that the chip cannot render.
 *
 * NOTHING ON THE PACE AXIS CARRIES A VALENCE, and that is a rule rather than an
 * oversight. `coachCopy` forbids positive certification (no on-track, no
 * doing-great, no green) and Manage inherits it; painting `ahead of pace` in
 * pear would certify a squad from the one surface that promised never to. Pace
 * is a comparison against the teacher's own lens, not a grade.
 *
 * PERSIMMON APPEARS EXACTLY ONCE in this whole table, on `not_reconciled`, and
 * that is the point of the audit's fourth defect: persimmon means error or
 * consequence and nothing else now, so the one state that IS a failure is the
 * one state that gets it.
 */
export const STATUS_VOCABULARY = {
  /* ---------------------------------------------------------------- work stage */
  planning: {
    term: "planning",
    tone: "info",
    axis: "stage",
    why: "already the shared word across coachCopy and the room reading; unchanged.",
  },
  executing: {
    term: "executing",
    tone: "info",
    axis: "stage",
    why: "unchanged. The audit found no second phrasing and no collision for this position on the stage axis.",
  },
  finishing: {
    term: "finishing",
    tone: "info",
    axis: "stage",
    why: "unchanged. The audit found no second phrasing and no collision for this position on the stage axis.",
  },
  turned_in: {
    term: "turned in",
    tone: "positive",
    axis: "stage",
    why: "the stage a squad reaches when work leaves its hands, graded or not. Kept over `submitted` because `submitted` is the wire word and reads as a form action; kept over `work turned in` because the noun is already implied by the column it sits in.",
  },

  /* -------------------------------------------------------------- review queue */
  awaiting_review: {
    term: "awaiting review",
    tone: "info",
    axis: "review",
    why: "the one name for submitted-not-graded, replacing seven. It names what the TEACHER owes rather than what the squad did, which is the only reason this state is on screen. Already the toolbar's word and the sidebar's screen-reader phrasing, so it is the least disruptive of the seven and the most accurate.",
  },
  reviewed: {
    term: "reviewed",
    tone: "positive",
    axis: "review",
    why: "the one name for graded, replacing four. Chosen over `graded` for symmetry: the surface is Review and its queue is `awaiting review`, so its completion must be `reviewed` or the pair reads as two different processes. A SCORE IS NOT A STATUS: `scored 87%` and `current score N%` were the same state wearing its value as a name, and the value now renders beside the term instead of replacing it.",
  },
  not_turned_in: {
    term: "not turned in",
    tone: "neutral",
    axis: "review",
    why: "the exact complement of `turned in`, so the two read as one axis. `missing` was wrong because nothing is lost, and `not submitted yet` is a sentence rather than a label and cannot sit in a count pill.",
  },

  /* --------------------------------------------------- level lifecycle (Review) */
  submissions_arriving: {
    term: "submissions arriving",
    tone: "info",
    axis: "lifecycle",
    why: "unchanged. Some squads in, some not, and no collision found.",
  },
  upcoming: {
    term: "upcoming",
    tone: "neutral",
    axis: "lifecycle",
    why: "a level above the expected pace with nothing submitted. The audit flagged a collision with `above` and `ahead`; it dissolves rather than needing a rename, because the pace axis now says `ahead of pace` and never says `above`.",
  },
  not_reconciled: {
    term: "not reconciled",
    tone: "critical",
    axis: "lifecycle",
    why: "THE `active` COLLISION, RESOLVED. `lifecycleForLevel` returns `active` when the level counts fail their own invariant (graded plus pending must equal submitted, submitted plus missing must equal squads) or when a count is absent. That is a data failure wearing the same word Coach uses for a squad that is working. Coach's sense wins, per the ruling, so this one is renamed: `not reconciled` states the actual condition, is the only entry in the table that takes persimmon, and can never be mistaken for a description of the room.",
  },

  /* ------------------------------------------------------- activity (Coach's) */
  active: {
    term: "active",
    tone: "info",
    axis: "activity",
    why: "the squad is working. COACH'S SENSE WINS for room states, per the ruling. Unchanged here precisely because the other claimant moved.",
  },
  quiet: {
    term: "quiet",
    tone: "neutral",
    axis: "activity",
    why: "unchanged, and deliberately never `stuck` or `stalled`. The word describes the record, not the squad.",
  },
  done: {
    term: "done",
    tone: "positive",
    axis: "activity",
    why: "activity closed. The audit noted `done` also appears in the task counter (`5 tasks, 3 done`) and in `done for now`; neither is a status label, so there is no state to split.",
  },
  no_activity_recorded: {
    term: "no activity recorded yet",
    tone: "neutral",
    axis: "activity",
    why: "unchanged. Distinct from `quiet`: nothing has been recorded, versus something was and has stopped.",
  },
  activity_not_reported: {
    term: "activity not reported",
    tone: "neutral",
    axis: "activity",
    why: "unchanged. The read did not carry the field, which is not the same as the squad having done nothing.",
  },

  /* ------------------------------------------------------------------- pace */
  ahead_of_pace: {
    term: "ahead of pace",
    tone: "neutral",
    axis: "pace",
    why: "ONE PACE PHRASING, replacing four (`ahead`, `ahead of expected pace`, `N above`, and the matrix band). `of pace` is carried rather than dropped because the bare word invites the question `ahead of what`, and the answer is the teacher's own lens rather than a syllabus.",
  },
  at_pace: {
    term: "at pace",
    tone: "neutral",
    axis: "pace",
    why: "replaces `at pace`, `your lens` and the zero case of `N above / N below`. THE LENS IS THE ANCHOR OF THE COMPARISON, NOT A STATE: a row that equals the expected level is at pace, and the band explains that the lens is where the comparison is taken from.",
  },
  behind_pace: {
    term: "behind pace",
    tone: "neutral",
    axis: "pace",
    why: "replaces `behind`, `behind expected pace` and `N below`. Neutral tone, not persimmon: behind is a position, not a failure, and colouring it as one would be the negative twin of the certification Coach forbids.",
  },
  pace_not_recorded: {
    term: "pace not recorded",
    tone: "neutral",
    axis: "pace",
    why: "one phrasing for what was two (`pace not recorded`, `expected pace not recorded`). The shorter form wins because the longer one appears in the command band where width is scarce.",
  },
  not_evidenced: {
    term: "not evidenced",
    tone: "neutral",
    axis: "pace",
    why: "one phrasing for what was two (`not evidenced`, `level not evidenced`). The squad has not produced evidence for a level, which is a statement about the record and not about the squad.",
  },

  /* -------------------------------------------------------------- placement */
  not_in_a_squad: {
    term: "not in a squad",
    tone: "neutral",
    axis: "placement",
    why: "one name for what was three (`not in a squad`, `in none`, `unplaced`). Plain over `unplaced`, which is a system word for a person.",
  },
  retired: {
    term: "retired",
    tone: "neutral",
    axis: "placement",
    why: "EARNED DIVERGENCE 5, PRESERVED. The wire says `archived` and the whole UI says `retire`, deliberately: `archived` reads as deletion to a teacher and archive destroys nothing. The UI word is the canonical one and the wire word never surfaces.",
  },

  /* --------------------------------------------------- authority and reach */
  owning_teacher: {
    term: "owning teacher",
    tone: "info",
    axis: "authority",
    why: "unchanged. The teacher may act on this work.",
  },
  read_only: {
    term: "read only",
    tone: "neutral",
    axis: "authority",
    why: "unchanged, but narrowed to ONE cause: the teacher does not own this squad. The second thing that used these words was not a permission at all, and it moved.",
  },
  showing_previous_read: {
    term: "showing the previous read",
    tone: "neutral",
    axis: "authority",
    why: "THE SECOND `read only`, SPLIT OUT. A refresh that failed left the queue labelled `read only`, which told the facilitator they lacked permission when in fact they had it and the numbers were merely older. The new phrase names the actual condition and matches the shell's stale notice, which describes the same failure one strip above.",
  },
  unavailable: {
    term: "not available",
    tone: "neutral",
    axis: "authority",
    why: "one phrasing for what was two (`not available`, `unavailable`). The audit noted four different CAUSES share this one word; that is left standing on purpose, because every render pairs the label with a sentence naming its own cause, and four labels for `we cannot show you this` would be four words to learn for one instruction: wait, or ask.",
  },
  locked: {
    term: "locked",
    tone: "neutral",
    axis: "authority",
    why: "unchanged. The reveal has happened and the artifact is fixed.",
  },
  no_cohort: {
    term: "no cohort resolved yet",
    tone: "neutral",
    axis: "authority",
    why: "one phrasing for what was two (`no cohorts assigned yet`, `no cohort resolved yet`), and the audit found both can render on one screen because the context bar sits above Coach. The bar's phrasing wins: it is the element present on every surface, and `resolved` is true whether the cause is no assignment or a read that has not landed.",
  },
};

/**
 * Every retired phrase, and the state it belongs to.
 *
 * This is the working list for FE-9.3 to FE-9.6. A rename is then a lookup
 * rather than a judgement re-made on three surfaces, and FE-9.6's cold sweep
 * can grep for a phrase on this list and expect zero hits outside a docblock.
 *
 * Keys are lowercased exact phrases as they render today. Values are keys of
 * `STATUS_VOCABULARY`, and the coverage test asserts every one of them resolves.
 */
export const RETIRED = {
  submitted: "turned_in",
  "work turned in": "turned_in",
  graded: "reviewed",
  "scored n%": "reviewed",
  "current score n%": "reviewed",
  "review needed": "awaiting_review",
  "work awaiting review": "awaiting_review",
  missing: "not_turned_in",
  "not submitted yet": "not_turned_in",
  ahead: "ahead_of_pace",
  "ahead of expected pace": "ahead_of_pace",
  "n above": "ahead_of_pace",
  behind: "behind_pace",
  "behind expected pace": "behind_pace",
  "n below": "behind_pace",
  "your lens": "at_pace",
  "expected pace not recorded": "pace_not_recorded",
  "level not evidenced": "not_evidenced",
  "in none": "not_in_a_squad",
  unplaced: "not_in_a_squad",
  archived: "retired",
  unavailable: "unavailable",
  "no cohorts assigned yet": "no_cohort",
  /* The lifecycle `active` is the one rename that changes a WORD's meaning
     rather than choosing between synonyms, so it is listed under the phrase it
     used to render as, alongside the note that Coach keeps the word. */
  "active (level counts did not reconcile)": "not_reconciled",
};

/** The one read both lookups make, so the refusal to guess is stated once.
 *  OWN PROPERTIES ONLY: a bare `STATUS_VOCABULARY[key]` resolves `__proto__` to
 *  `Object.prototype` and `constructor` to `Object`, both truthy, so a
 *  truthiness guard alone lets a name every object already has pass as a state
 *  and return `undefined` from a function documented to throw. */
const statusEntry = (key) => {
  if (!Object.prototype.hasOwnProperty.call(STATUS_VOCABULARY, key)) {
    throw new Error(`unknown facilitator status: ${key}`);
  }
  return STATUS_VOCABULARY[key];
};

/** The canonical term for a state. Throws on an unknown key rather than
 *  returning the key: a status that renders as `awaiting_reviw` on a typo is a
 *  defect that ships, and a status that throws is one that never leaves a test. */
export const statusTerm = (key) => statusEntry(key).term;

/** The `StatusChip` tone for a state. Same contract as `statusTerm`. */
export const statusTone = (key) => statusEntry(key).tone;

export const STATUS_KEYS = Object.keys(STATUS_VOCABULARY);

/**
 * THE PACE COMPARISON AS ONE SENTENCE, DISTANCE INCLUDED.
 *
 * The pace axis has three terms and three renders that need the DISTANCE
 * alongside the term. FE-9.3 to FE-9.5 each reached for `statusTerm` and then
 * hand-composed the rest, so `RoomMatrix.deltaLabel`, `SquadDrawer`'s
 * where-line and `attentionRule`'s pace fact carried three transcriptions of
 * one grammar (the count, the `level` / `levels` plural, the term, in that
 * order). Three copies of a sentence is how a fourth phrasing of the pace
 * comparison gets born, which is the exact defect the pace entries above exist
 * to have ended. The stage-1 advisory on FE-9.5 named it; this is the
 * extraction.
 *
 * WHAT IT DOES NOT DO: it never says `level N` and never says `your lens`. The
 * subject belongs to the caller, because the matrix labels a ROW, the drawer
 * labels a SQUAD and the attention opener labels neither. The lens is the
 * anchor of the comparison, not part of it.
 *
 * @param {number} levelNumber the level actually evidenced
 * @param {number} expectedLevel the teacher's expected level
 * @returns {string|null} `at pace`, `1 level behind pace`, `3 levels ahead of
 *   pace`, or null when either side of the comparison is absent or is not a
 *   valid level. NULL RATHER THAN A GUESS: `Number(null)` is 0 and would render
 *   `2 levels ahead of pace` out of a missing field, which is a specific claim
 *   made from an absence. A level is a positive 1-based integer, so 0, -1 and
 *   1.5 are refused on the same grounds.
 */
export const paceDistance = (levelNumber, expectedLevel) => {
  /* THE DOMAIN IS A LEVEL, NOT A FINITE NUMBER. `validLevelNumber` in
     `Dashboard/coachCopy.js` is this codebase's definition of a level and it is
     `Number.isInteger(value) && value >= 1`, because a level IS a capsule's
     `level_number`. A finiteness test admits 0, -1 and 1.5, each of which
     renders a confident pace sentence out of a value no level can hold.
     It returns null rather than throwing, for the same reason the absence
     cases do and for the reason `validLevelNumber` does: both arguments are
     wire-derived, so an impossible value is a bad row rather than a caller
     bug, and refusing to speak about it is right where crashing the render
     around it is not. `statusTerm` throws because ITS argument is a literal a
     developer wrote. */
  if (!Number.isInteger(levelNumber) || !Number.isInteger(expectedLevel)) {
    return null;
  }
  if (levelNumber < 1 || expectedLevel < 1) {
    return null;
  }
  if (levelNumber === expectedLevel) return statusTerm("at_pace");
  const distance = Math.abs(levelNumber - expectedLevel);
  const term = statusTerm(
    levelNumber > expectedLevel ? "ahead_of_pace" : "behind_pace",
  );
  return `${distance} ${distance === 1 ? "level" : "levels"} ${term}`;
};
